import { apiGet } from './auth'

/** One shop's live 4-Wall headline contribution. */
export interface FourWallStore {
  store_id: number
  store_code: string
  store_name: string
  sales_eur: number | null
  col_eur: number | null
  col_pct: number | null
  worked_hours: number | null
  grid_hours: number | null
  grid_diff: number | null
  grid_status: 'matched' | 'below_minimum' | 'above_maximum' | null
  revenue_available: boolean
  labour_available: boolean
  sales_error_code: string | null
  sales_error_message: string | null
  labour_error_code: string | null
  labour_error_message: string | null
  /**
   * Where this shop's sales came from: the VM Hub archive, Butlaroo, or both across
   * the cutover. Null when neither system knows the shop in this period.
   */
  sales_source: SalesSource | null
}

/** Which system supplied a period figure. */
export type SalesSource = 'butlaroo' | 'vmhub_history' | 'mixed'

export interface FourWallTotals {
  sales_eur: number | null
  col_eur: number | null
  /** Cost ÷ sales, only across shops that supplied both. */
  col_pct: number | null
  worked_hours: number | null
  grid_hours: number | null
  /** Worked minus grid, only across shops that supplied both. */
  grid_diff: number | null
  stores_with_sales: number
  stores_with_labour: number
  stores_with_col: number
  complete: boolean
}

/** Response of `GET /api/v1/overview/four-wall`. */
export interface FourWallResponse {
  start_date: string
  end_date: string
  today: string
  currency: string
  vat_included: boolean
  stores_requested: number
  stores: FourWallStore[]
  totals: FourWallTotals
  warnings: string[]
  /** Which system supplied the period sales (archive, vendor, or both). */
  sales_source: SalesSource | null
  source: 'butlaroo_insights' | 'vmhub_history' | 'mixed'
  labour_source: 'shiftbase_timesheets_roster'
  grid_source: 'col_labor_grid'
  aggregation: 'vendor_blackbox' | 'stored_aggregates' | 'mixed'
  fetched_at: string
}

export interface FourWallParams {
  from: string
  to: string
  /** Omit to let the server use the account's whole entitled scope. */
  storeIds?: number[]
  /** TanStack Query passes this so a superseded period aborts its fetch. */
  signal?: AbortSignal
}

export function fetchFourWallKpis({
  from,
  to,
  storeIds,
  signal,
}: FourWallParams): Promise<FourWallResponse> {
  return apiGet<FourWallResponse>(
    '/overview/four-wall',
    {
      start_date: from,
      end_date: to,
      store_ids: storeIds && storeIds.length > 0 ? storeIds : undefined,
    },
    signal,
  )
}
